import React, { useState, useEffect, useMemo } from 'react';
import { cuotaIglesiaService, HistoricoMensual } from '../services/cuotaIglesiaService';
import { movimientoService } from '../services/movimientoService';
import { supabase } from '../lib/supabase';
import { Movimiento } from '../lib/supabase';
import { Tabs } from './ui/Tabs';
import { Select } from './ui/Select';
import { EditarAporteModal } from './EditarAporteModal';
import { DangerConfirmDialog } from './ui/DangerConfirmDialog';
import { formatDate } from '../utils/formatters';
import { matchesSearch } from '../utils/textNormalization';

interface AportesHistoricoMensualProps {
  readonly lumisialId: string;
  /** Editar y anular aportes: solo quien opera el dinero de este Lumisial. */
  readonly puedeOperar: boolean;
  /** Año con el que abrir el histórico. Sin él, el año en curso. Lo trae el período que
   *  viene desde Balance: sin esto, entrar desde un mes de 2025 abría el histórico en 2026. */
  readonly añoInicial?: number;
}

interface MovimientoCartola extends Movimiento {
  nombre_display: string;
}

export const AportesHistoricoMensual: React.FC<AportesHistoricoMensualProps> = ({ lumisialId, añoInicial, puedeOperar }) => {
  const [subVista, setSubVista] = useState<'mensual' | 'cartola'>('mensual');

  // ── Vista Mensual ──────────────────────────────────────────────
  const [historicoMensual, setHistoricoMensual] = useState<HistoricoMensual[]>([]);
  const [añoHistorico, setAñoHistorico] = useState(añoInicial ?? new Date().getFullYear());
  const [loadingHistorico, setLoadingHistorico] = useState(false);

  useEffect(() => {
    if (lumisialId) {
      loadHistorico(añoHistorico);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lumisialId, añoHistorico]);

  const loadHistorico = async (año: number) => {
    if (!lumisialId) return;

    try {
      setLoadingHistorico(true);
      const historico = await cuotaIglesiaService.getHistoricoMensual(lumisialId, año);
      setHistoricoMensual(historico);
    } catch (error) {
      console.error('Error loading histórico:', error);
    } finally {
      setLoadingHistorico(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(amount);
  };

  // ── Vista Cartola ──────────────────────────────────────────────
  const [cartolaDatos, setCartolaDatos] = useState<MovimientoCartola[]>([]);
  const [cartolaLoading, setCartolaLoading] = useState(false);
  const [cartolaError, setCartolaError] = useState<string | null>(null);
  const [filtroNombre, setFiltroNombre] = useState('');
  const [filtroTipo, setFiltroTipo] = useState<'todos' | 'miembros' | 'externos'>('todos');
  const [movimientoEditando, setMovimientoEditando] = useState<Movimiento | null>(null);
  const [movimientoAnulando, setMovimientoAnulando] = useState<MovimientoCartola | null>(null);
  const [errorAnulacion, setErrorAnulacion] = useState<string | null>(null);

  const cargarCartola = async () => {
    if (!lumisialId) return;
    try {
      setCartolaLoading(true);
      setCartolaError(null);

      const { data, error } = await supabase
        .from('movimientos')
        .select('*, miembro:origen_id(nombres, apellidos)')
        .eq('lumisial_id', lumisialId)
        .eq('tipo', 'ingreso')
        .eq('anulado', false)
        .in('origen_tipo', ['miembro', 'externo'])
        .gte('fecha', `${añoHistorico}-01-01`)
        .lte('fecha', `${añoHistorico}-12-31`)
        .order('fecha', { ascending: false });

      if (error) throw error;

      const movimientos: MovimientoCartola[] = (data ?? []).map((m) => {
        const miembro = Array.isArray(m.miembro) ? m.miembro[0] : m.miembro;
        const nombre_display =
          m.origen_tipo === 'miembro' && miembro
            ? `${miembro.nombres} ${miembro.apellidos}`
            : (m.nombre_externo || 'Externo');
        return { ...m, nombre_display } as MovimientoCartola;
      });

      setCartolaDatos(movimientos);
    } catch (e) {
      setCartolaError(e instanceof Error ? e.message : 'Error al cargar la cartola');
    } finally {
      setCartolaLoading(false);
    }
  };

  useEffect(() => {
    if (lumisialId && subVista === 'cartola') {
      cargarCartola();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lumisialId, subVista, añoHistorico]);

  const cartolaFiltrada = useMemo(() => {
    return cartolaDatos.filter(m => {
      const matchNombre =
        filtroNombre === '' ||
        matchesSearch(m.nombre_display, filtroNombre);
      const matchTipo =
        filtroTipo === 'todos' ||
        (filtroTipo === 'miembros' && m.origen_tipo === 'miembro') ||
        (filtroTipo === 'externos' && m.origen_tipo === 'externo');
      return matchNombre && matchTipo;
    });
  }, [cartolaDatos, filtroNombre, filtroTipo]);

  const cartolaAgrupada = useMemo(() => {
    const grupos: { mesLabel: string; subtotal: number; movimientos: MovimientoCartola[] }[] = [];
    const mesesMap = new Map<string, MovimientoCartola[]>();

    cartolaFiltrada.forEach(m => {
      const key = m.fecha.slice(0, 7);
      if (!mesesMap.has(key)) mesesMap.set(key, []);
      mesesMap.get(key)!.push(m);
    });

    const keysOrdenados = Array.from(mesesMap.keys()).sort((a, b) => b.localeCompare(a));

    keysOrdenados.forEach(key => {
      const movs = mesesMap.get(key)!;
      const [año, mes] = key.split('-');
      const fecha = new Date(Number.parseInt(año), Number.parseInt(mes) - 1, 1);
      const mesLabel = fecha.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' });
      const subtotal = movs.reduce((sum, m) => sum + Number(m.monto), 0);
      grupos.push({
        mesLabel: mesLabel.charAt(0).toUpperCase() + mesLabel.slice(1),
        subtotal,
        movimientos: movs,
      });
    });

    return grupos;
  }, [cartolaFiltrada]);

  const totalCartola = cartolaFiltrada.reduce((sum, m) => sum + Number(m.monto), 0);

  return (
    <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
      <Tabs
        tabs={[{ id: 'mensual', label: 'Mensual' }, { id: 'cartola', label: 'Cartola' }]}
        activeTab={subVista}
        onChange={(id) => setSubVista(id as 'mensual' | 'cartola')}
        variant="pills"
        className="mb-4"
      />

      {/* ── Sub-vista Mensual ─────────────────────────────────── */}
      {subVista === 'mensual' && (
        <>
          <div className="mb-6 flex items-center gap-4">
            <Select
              label="Año"
              options={Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((año) => ({
                value: String(año),
                label: String(año),
              }))}
              value={String(añoHistorico)}
              onChange={(v) => setAñoHistorico(Number(v))}
              placeholder=""
            />
            <div className="ml-auto text-sm text-slate-600">
              12 meses • Año {añoHistorico}
            </div>
          </div>

          {loadingHistorico ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Mes</th>
                    <th className="px-6 py-3 text-right text-sm font-semibold text-blue-700">Aportes Mensuales</th>
                    <th className="px-6 py-3 text-right text-sm font-semibold text-amber-700">Cuota Iglesia</th>
                    <th className="px-6 py-3 text-right text-sm font-semibold text-slate-700">Total Recaudado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {historicoMensual.map((dato) => {
                    const fecha = new Date(dato.año, dato.mes - 1);
                    const mesLargo = fecha.toLocaleDateString('es-CL', { month: 'long' });
                    const mesCorto = fecha.toLocaleDateString('es-CL', { month: 'short' }).replace('.', '');
                    const nombreMesLargo = mesLargo.charAt(0).toUpperCase() + mesLargo.slice(1);
                    const nombreMesCorto = mesCorto.charAt(0).toUpperCase() + mesCorto.slice(1);

                    return (
                      <tr key={`${dato.año}-${dato.mes}`} className="hover:bg-slate-50">
                        <td className="px-6 py-4 text-sm font-medium text-slate-800">
                          <span className="sm:hidden whitespace-nowrap">{nombreMesCorto} {dato.año}</span>
                          <span className="hidden sm:inline whitespace-nowrap">{nombreMesLargo} {dato.año}</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-blue-800 text-right font-bold">
                          {formatCurrency(dato.aportes_mensuales)}
                        </td>
                        <td className="px-6 py-4 text-sm text-amber-800 text-right font-bold">
                          {formatCurrency(dato.cuota_iglesia)}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-800 text-right font-bold">
                          {formatCurrency(dato.total_recaudado)}
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="bg-slate-100 font-bold">
                    <td className="px-6 py-4 text-sm text-slate-900">Total Anual</td>
                    <td className="px-6 py-4 text-sm text-blue-900 text-right">
                      {formatCurrency(historicoMensual.reduce((sum, d) => sum + d.aportes_mensuales, 0))}
                    </td>
                    <td className="px-6 py-4 text-sm text-amber-900 text-right">
                      {formatCurrency(historicoMensual.reduce((sum, d) => sum + d.cuota_iglesia, 0))}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-900 text-right">
                      {formatCurrency(historicoMensual.reduce((sum, d) => sum + d.total_recaudado, 0))}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Sub-vista Cartola ─────────────────────────────────── */}
      {subVista === 'cartola' && (
        <>
          {/* Controles */}
          <div className="flex flex-wrap gap-3 mb-4">
            <Select
              options={Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((año) => ({
                value: String(año),
                label: String(año),
              }))}
              value={String(añoHistorico)}
              onChange={(v) => setAñoHistorico(Number(v))}
              placeholder=""
            />
            <input
              type="text"
              placeholder="Buscar por nombre..."
              value={filtroNombre}
              onChange={e => setFiltroNombre(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm flex-1 min-w-[180px]"
            />
            <Select
              options={[
                { value: 'todos', label: 'Todos' },
                { value: 'miembros', label: 'Miembros' },
                { value: 'externos', label: 'Externos' },
              ]}
              value={filtroTipo}
              onChange={(v) => setFiltroTipo(v as typeof filtroTipo)}
              placeholder="Todos"
            />
            <div className="text-sm font-semibold text-slate-700 flex items-center">
              Total: {formatCurrency(totalCartola)}
            </div>
          </div>

          {cartolaLoading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}

          {cartolaError && !cartolaLoading && (
            <div className="text-center py-12 text-red-600">{cartolaError}</div>
          )}

          {!cartolaLoading && !cartolaError && cartolaDatos.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              No hay aportes registrados en {añoHistorico}
            </div>
          )}

          {!cartolaLoading && !cartolaError && cartolaDatos.length > 0 && cartolaFiltrada.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              No se encontraron aportes para &ldquo;{filtroNombre}&rdquo;
            </div>
          )}

          {!cartolaLoading && !cartolaError && cartolaAgrupada.map(grupo => (
            <div key={grupo.mesLabel} className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-sm font-bold text-slate-600 uppercase tracking-wide">{grupo.mesLabel}</span>
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-sm font-semibold text-slate-700">{formatCurrency(grupo.subtotal)}</span>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {grupo.movimientos.map(mov => (
                    <tr key={mov.id} className="hover:bg-slate-50 border-b border-slate-100">
                      <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">
                        {formatDate(mov.fecha, { day: '2-digit', month: 'short' })}
                      </td>
                      <td className="py-2 pr-3 font-medium text-slate-800">{mov.nombre_display}</td>
                      <td className="py-2 pr-3">
                        {mov.origen_tipo === 'miembro' ? (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            mov.categoria_aporte === 'aporte_mensual'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}>
                            {mov.categoria_aporte === 'aporte_mensual' ? 'Aporte Lumisial' : 'Cuota Iglesia'}
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                            Externo
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-slate-500 text-xs max-w-[160px] truncate">{mov.descripcion || '—'}</td>
                      <td className="py-2 pr-3 font-semibold text-slate-800 text-right whitespace-nowrap">
                        {formatCurrency(Number(mov.monto))}
                      </td>
                      <td className="py-2 text-right whitespace-nowrap">
                        {puedeOperar && (<>
                        <button
                          onClick={() => setMovimientoEditando(mov)}
                          className="px-2 py-1 text-xs border border-blue-300 text-blue-700 rounded hover:bg-blue-50 mr-1"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => { setMovimientoAnulando(mov); setErrorAnulacion(null); }}
                          className="px-2 py-1 text-xs border border-red-300 text-red-700 rounded hover:bg-red-50"
                        >
                          🚫
                        </button>
                        </>)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

          {/* Modal edición */}
          {movimientoEditando && (
            <EditarAporteModal
              isOpen={!!movimientoEditando}
              onClose={() => setMovimientoEditando(null)}
              onSuccess={() => { setMovimientoEditando(null); void cargarCartola(); }}
              movimiento={movimientoEditando}
            />
          )}

          {/* Dialog anulación */}
          <DangerConfirmDialog
            isOpen={!!movimientoAnulando}
            onClose={() => { setMovimientoAnulando(null); setErrorAnulacion(null); }}
            onConfirm={async () => {
              if (!movimientoAnulando) return;
              try {
                await movimientoService.anular(movimientoAnulando.id);
                setMovimientoAnulando(null);
                void cargarCartola();
              } catch (e) {
                setErrorAnulacion(e instanceof Error ? e.message : 'Error al anular');
              }
            }}
            title="Anular aporte"
            message={`¿Anular el aporte de ${movimientoAnulando?.nombre_display} del ${movimientoAnulando ? formatDate(movimientoAnulando.fecha) : ''}?`}
            warningItems={['El aporte quedará marcado como anulado', 'Los totales se actualizarán']}
            checkboxLabel="Entiendo que esta acción no se puede deshacer"
            confirmText="Confirmar anulación"
            cancelText="Cancelar"
          />

          {errorAnulacion && movimientoAnulando && (
            <div className="fixed bottom-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg z-[100]">
              {errorAnulacion}
            </div>
          )}
        </>
      )}
    </div>
  );
};
